/* eslint-disable tailwindcss/no-custom-classname */
import IconSortAscending from '@apollo/icons/small/IconSortAscending.svg';
import IconSortDescending from '@apollo/icons/small/IconSortDescending.svg';
import { forwardRef, useTableStyles } from '@chakra-ui/react';
import { callAllHandlers } from '@chakra-ui/utils';
import classnames from 'classnames';
import React from 'react';

import { SortOrder } from '../../types';
import { ButtonProps } from '../Button/Button';
import { IconButton } from '../IconButton/IconButton';
import { useTableContext } from '../Table/Table.context';
import { Tooltip } from '../Tooltip/Tooltip';

export interface TableSortButtonProps
  extends Omit<ButtonProps, 'variant' | 'size' | '__orbitStyleConfig'> {
  attribute: string | number;
  defaultSortOrder?: SortOrder;
}

function getTooltipLabel(
  sortOrder: SortOrder,
  defaultSortOrder: SortOrder,
  isCurrentSortedAttribute: boolean,
) {
  if (!isCurrentSortedAttribute) {
    return `Sort in ${
      defaultSortOrder === 'asc' ? 'ascending' : 'descending'
    } order`;
  }

  return `Sorted in ${sortOrder === 'asc' ? 'ascending' : 'descending'} order`;
}

export const TableSortButton = forwardRef<TableSortButtonProps, 'button'>(
  (
    { attribute, defaultSortOrder = 'asc', className, onClick, ...props },
    ref,
  ) => {
    const { sortBy, sortOrder, setSort } = useTableContext();
    const isCurrentSortedAttribute = sortBy === attribute;
    const tooltipLabel = getTooltipLabel(
      sortOrder,
      defaultSortOrder,
      isCurrentSortedAttribute,
    );
    const styles = useTableStyles();

    return (
      <Tooltip label={tooltipLabel} placement="top">
        <IconButton
          {...props}
          data-is-current={isCurrentSortedAttribute}
          className={classnames('orbit-table__sort-button', className)}
          ref={ref}
          aria-label={tooltipLabel}
          size="xs"
          onClick={callAllHandlers(onClick, (e) =>
            setSort(
              attribute,
              isCurrentSortedAttribute
                ? sortOrder === 'asc'
                  ? 'desc'
                  : 'asc'
                : defaultSortOrder,
              e,
            ),
          )}
          variant="hidden"
          icon={
            <SortIcon
              sortOrder={
                isCurrentSortedAttribute ? sortOrder : defaultSortOrder
              }
            />
          }
          __orbitStyleConfig={styles.sortButton}
        />
      </Tooltip>
    );
  },
);

const SortIcon = ({ sortOrder }: { sortOrder: SortOrder }) => {
  return sortOrder === 'asc' ? <IconSortAscending /> : <IconSortDescending />;
};
